import { pageTitle } from '@palaccount/config';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api } from '@/api';
import { BrandMark } from '@/components/brand-mark';
import { LanguageSwitch } from '@/components/language-switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUiStore } from '@/lib/ui-store';

/**
 * GEN-S1 "Forgot password?", step 1 — the screen the spec leaves as "[NOT IN
 * PROTOTYPE — decide at build time]".
 *
 * ONE OUTCOME, ALWAYS. Type anything and this screen says the same sentence:
 * "if that email address has an account, a reset link is on its way." That is
 * not vagueness, it is the requirement — DECISION.md §5.16 asks for "a generic
 * 200 always", because a screen that says "no such account" hands an attacker
 * a customer list one address at a time. The server cannot express any other
 * answer either, so there is nothing here to keep in step with it.
 *
 * The only failure this screen can show is the per-connection rate limit,
 * which says nothing about any address.
 *
 * The language the person is standing in travels with the request. It is not
 * what decides the email's language — the recipient's own stored language
 * wins for an account that exists — but for the one case where they are the
 * same person it is the best signal there is.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const locale = useUiStore((state) => state.locale);

  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('reset.askTitle'));
  }, [t]);

  const submit = async () => {
    setError(null);
    setPending(true);
    try {
      const response = await api.auth.forgotPassword({ body: { email, language: locale } });
      if (response.status === 429) {
        setError(t('reset.rateLimited'));
        return;
      }
      setSent(true);
    } catch {
      // A network failure is the one thing that is genuinely our problem, and
      // it must not be dressed up as the generic success sentence.
      setError(t('reset.rateLimited'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="w-full max-w-md rounded-xl border bg-background p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <BrandMark />
          <LanguageSwitch />
        </div>

        {sent ? (
          <div className="grid gap-4">
            <h1 className="text-xl font-bold">{t('reset.sentTitle')}</h1>
            {/* The same sentence the server returns, whatever it found. */}
            <p className="text-sm text-muted-foreground">{t('reset.sentBody')}</p>
            <Link className="text-sm text-primary underline" to="/sign-in">
              {t('auth.signIn')}
            </Link>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <h1 className="text-xl font-bold">{t('reset.askTitle')}</h1>
            <p className="text-sm text-muted-foreground">{t('reset.askSub')}</p>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="reset-email">{t('auth.emailLabel')}</Label>
              <Input
                id="reset-email"
                type="email"
                dir="ltr"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={pending || email.trim().length === 0}>
              {pending ? <Loader2 aria-hidden className="animate-spin" /> : null}
              {t('reset.askAction')}
            </Button>
            <Link className="text-sm text-primary underline" to="/sign-in">
              {t('reset.backToSignIn')}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
